import { createContext, useContext, useMemo, useState } from "react";

/** The in-flight PDF import being tracked app-wide (one at a time). */
export interface ActiveImport {
  jobId: string;
  parentId: string; // the Project to navigate to
  fileName: string;
}

interface PdfImportContextValue {
  active: ActiveImport | null;
  /** true = show the expanded card; false = show the minimized corner pill. */
  expanded: boolean;
  start: (imp: ActiveImport) => void;
  expand: () => void;
  minimize: () => void;
  dismiss: () => void;
}

const PdfImportContext = createContext<PdfImportContextValue | null>(null);

export function usePdfImport(): PdfImportContextValue {
  const ctx = useContext(PdfImportContext);
  if (!ctx) throw new Error("usePdfImport must be used within a PdfImportProvider");
  return ctx;
}

/**
 * App-level tracker for a running PDF import so its progress survives closing the upload modal and
 * navigating between pages. The polling + UI live in <PdfImportTracker/>; this only holds the state.
 */
export function PdfImportProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState<ActiveImport | null>(null);
  const [expanded, setExpanded] = useState(true);

  const value = useMemo<PdfImportContextValue>(
    () => ({
      active,
      expanded,
      start: (imp) => {
        setActive(imp);
        setExpanded(true);
      },
      expand: () => setExpanded(true),
      minimize: () => setExpanded(false),
      dismiss: () => {
        setActive(null);
        setExpanded(true);
      },
    }),
    [active, expanded]
  );

  return <PdfImportContext.Provider value={value}>{children}</PdfImportContext.Provider>;
}
